import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CalendarDays, Clock, MapPin, AlertTriangle, Users, Info } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MeetingRequestDialog — compose a 1:1 meeting at an event.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   event: { event_id, title, date, meeting_durations }
 *   recipient: { user_id, name }
 *   onCreated: (meeting_request) => void
 */
export default function MeetingRequestDialog({ open, onClose, event, recipient, onCreated }) {
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('session_token') : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const allowedDurations = (event?.meeting_durations && event.meeting_durations.length > 0)
    ? event.meeting_durations
    : [15, 30];

  const eventDate = event?.date || event?.start_date || new Date().toISOString().slice(0, 10);
  const normalisedDate = String(eventDate).slice(0, 10);

  const [date, setDate] = useState(normalisedDate);
  const [startTime, setStartTime] = useState('10:00');
  const [duration, setDuration] = useState(allowedDurations[0]);
  const [meetingPoint, setMeetingPoint] = useState('');
  const [message, setMessage] = useState('');
  const [clashes, setClashes] = useState({ requester_clashes: [], recipient_clashes: [] });
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Both parties' availability windows for the current date. When the
  // recipient has set at least one window, we surface them as pickable
  // chips so the requester doesn't guess at times that'll be rejected.
  const [myWindows, setMyWindows] = useState([]);
  const [theirWindows, setTheirWindows] = useState([]);

  // Reset the form each time the dialog is reopened with a different
  // recipient so stale state doesn't leak between invocations.
  useEffect(() => {
    if (!open) return;
    setDate(normalisedDate);
    setStartTime('10:00');
    setDuration(allowedDurations[0]);
    setMeetingPoint('');
    setMessage('');
    setClashes({ requester_clashes: [], recipient_clashes: [] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipient?.user_id, normalisedDate]);

  // Load both parties' availability windows when the dialog opens or the
  // recipient changes. Scope: window rows for the currently chosen date.
  useEffect(() => {
    if (!open || !event?.event_id || !recipient?.user_id) return;
    const headers = authHeaders;
    (async () => {
      try {
        const [mine, theirs] = await Promise.all([
          fetch(`/api/events/${event.event_id}/availability/me`, { credentials: 'include', headers }).then(r => r.ok ? r.json() : { windows: [] }),
          fetch(`/api/events/${event.event_id}/availability/${recipient.user_id}`, { credentials: 'include', headers }).then(r => r.ok ? r.json() : { windows: [] }),
        ]);
        setMyWindows(mine.windows || []);
        setTheirWindows(theirs.windows || []);
      } catch { /* non-fatal — dialog degrades to free-type */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.event_id, recipient?.user_id]);

  // Live clash preview — debounced to keep traffic sane while the member
  // nudges the time picker.
  useEffect(() => {
    if (!open || !event?.event_id || !recipient?.user_id) return;
    if (!/^\d{2}:\d{2}$/.test(startTime)) return;
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const url = `/api/events/${event.event_id}/clash-check` +
          `?date=${encodeURIComponent(date)}` +
          `&start_time=${encodeURIComponent(startTime)}` +
          `&duration_minutes=${duration}` +
          `&recipient_id=${recipient.user_id}`;
        const res = await fetch(url, { credentials: 'include', headers: authHeaders });
        if (res.ok) setClashes(await res.json());
      } finally { setChecking(false); }
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.event_id, recipient?.user_id, date, startTime, duration]);

  const totalClashes = (clashes.requester_clashes?.length || 0) + (clashes.recipient_clashes?.length || 0);

  const endTime = useMemo(() => {
    const [h, m] = (startTime || '').split(':').map(n => parseInt(n, 10) || 0);
    const total = h * 60 + m + duration;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }, [startTime, duration]);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!/^\d{2}:\d{2}$/.test(startTime)) { toast.error('Pick a valid start time.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/events/${event.event_id}/meeting-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: recipient.user_id,
          proposed_date: date,
          start_time: startTime,
          duration_minutes: duration,
          meeting_point: meetingPoint.trim() || null,
          message: message.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not send request');
      toast.success(`Meeting request sent to ${recipient.name}.`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="meeting-request-dialog">
        <DialogHeader>
          <DialogTitle>Request to meet · {recipient?.name}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            They'll get a notification to accept or decline. Accepted meetings appear on both of your event schedules.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="mr-date" className="text-xs">Date</Label>
              <div className="relative">
                <CalendarDays className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input id="mr-date" data-testid="mr-date" type="date" value={date}
                  onChange={e => setDate(e.target.value)} className="pl-7" />
              </div>
            </div>
            <div>
              <Label htmlFor="mr-start" className="text-xs">Start time</Label>
              <div className="relative">
                <Clock className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input id="mr-start" data-testid="mr-start-time" type="time" value={startTime}
                  onChange={e => setStartTime(e.target.value)} className="pl-7" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">Duration</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {allowedDurations.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  data-testid={`mr-duration-${d}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    duration === d
                      ? 'bg-brand-navy text-white border-brand-navy'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {d} min
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1" data-testid="mr-end-preview">
              Ends at <strong className="font-mono">{endTime}</strong>
            </p>
          </div>

          {/* Recipient's publicly-set networking windows for the chosen
              date. Tapping a chip prefills `startTime` with the window
              start so most members just pick-and-send. If neither party
              has set windows for this date, the hint quietly disappears. */}
          {(() => {
            const theirs = (theirWindows || []).filter(w => w.date === date);
            const mine = (myWindows || []).filter(w => w.date === date);
            if (theirs.length === 0 && mine.length === 0) return null;
            return (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 space-y-1.5" data-testid="mr-availability-hint">
                {theirs.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{recipient?.name}'s available windows</p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {theirs.map((w, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setStartTime(w.start_time)}
                          data-testid={`mr-their-window-${i}`}
                          className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                        >
                          {w.start_time}–{w.end_time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {mine.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Your available windows</p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {mine.map((w, i) => (
                        <span key={i} data-testid={`mr-my-window-${i}`}
                          className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600">
                          {w.start_time}–{w.end_time}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {theirs.length === 0 && mine.length > 0 && (
                  <p className="text-[10px] text-slate-400 italic flex items-center gap-1 pt-1 border-t border-slate-200">
                    <Info className="h-3 w-3" /> {recipient?.name} hasn't set windows for this date — any time can be proposed.
                  </p>
                )}
              </div>
            );
          })()}

          <div>
            <Label htmlFor="mr-point" className="text-xs">
              Meeting point {(!event?.meeting_points || event.meeting_points.length === 0) && <span className="text-slate-400">(optional)</span>}
            </Label>
            {event?.meeting_points && event.meeting_points.length > 0 ? (
              // Predefined — admin has published meeting-point options.
              <div className="relative">
                <MapPin className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                <select
                  id="mr-point"
                  data-testid="mr-meeting-point-select"
                  value={meetingPoint}
                  onChange={e => setMeetingPoint(e.target.value)}
                  className="w-full h-10 pl-7 pr-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
                >
                  <option value="">Pick a meeting point…</option>
                  {event.meeting_points.map(mp => (
                    <option key={mp} value={mp}>{mp}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="relative">
                <MapPin className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input id="mr-point" data-testid="mr-meeting-point" placeholder="e.g. Reception, Café on level 2"
                  value={meetingPoint} onChange={e => setMeetingPoint(e.target.value)} className="pl-7" />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="mr-note" className="text-xs">Note <span className="text-slate-400">(optional)</span></Label>
            <Textarea id="mr-note" data-testid="mr-message" rows={2}
              placeholder="Say hi, or tell them what you'd like to discuss."
              value={message} onChange={e => setMessage(e.target.value)} />
          </div>

          {/* Clash warnings — never block, always warn clearly */}
          {totalClashes > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs" data-testid="mr-clash-warning">
              <p className="flex items-center gap-1.5 font-semibold text-amber-800 mb-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Time clash detected
              </p>
              {clashes.requester_clashes?.length > 0 && (
                <div className="mb-1">
                  <p className="text-[11px] text-amber-700 font-semibold">On your schedule:</p>
                  <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                    {clashes.requester_clashes.map((c, i) => (
                      <li key={i} data-testid={`mr-clash-mine-${i}`}>
                        {c.title} <span className="text-amber-600">{c.start_time}–{c.end_time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {clashes.recipient_clashes?.length > 0 && (
                <div>
                  <p className="text-[11px] text-amber-700 font-semibold">On {recipient?.name}'s schedule:</p>
                  <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                    {clashes.recipient_clashes.map((c, i) => (
                      <li key={i} data-testid={`mr-clash-theirs-${i}`}>
                        {c.title} <span className="text-amber-600">{c.start_time}–{c.end_time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[11px] text-amber-700 mt-1 italic">You can still send the request — they may decline, or you can adjust later.</p>
            </div>
          )}
          {checking && totalClashes === 0 && (
            <p className="text-[11px] text-slate-400 italic">Checking schedule…</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="mr-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="mr-submit"
              className="bg-brand-navy hover:bg-brand-dark text-white">
              <Users className="h-4 w-4 mr-1.5" /> {submitting ? 'Sending…' : 'Send request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
